import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSEO } from '../../hooks/useSEO';
import { generatePropertyListSchema, generateBreadcrumbSchema, generateServiceSchema } from '../../utils/seo';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';

interface Imovel {
  id: string;
  codigo: string;
  titulo: string;
  slug: string;
  tipo: string;
  cidade: string;
  bairro: string;
  preco: number;
  quartos: number;
  banheiros: number;
  vagas: number;
  area_total: number;
  area_construida: number;
  destaque: boolean;
  fotos: { url: string }[];
}

export default function ComprarImovelSergipePage() {
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadImoveis();
  }, []);

  const loadImoveis = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('imoveis')
        .select(`
          *,
          fotos:imovel_fotos(url)
        `)
        .eq('status', 'disponivel')
        .order('created_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      setImoveis(data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    } finally {
      setLoading(false);
    }
  };

  // SEO
  useSEO({
    title: 'Comprar Imóvel em Sergipe - Aracaju e Barra dos Coqueiros | Mateus Oliveira',
    description: 'Compre seu imóvel em Sergipe com segurança e atendimento especializado. Casas e apartamentos de médio e alto padrão em Aracaju e Barra dos Coqueiros. Financiamento facilitado. WhatsApp (79) 99988-1002',
    keywords: 'comprar imóvel sergipe, comprar casa aracaju, comprar apartamento sergipe, imóveis à venda aracaju, comprar imóvel barra dos coqueiros, financiamento imóvel sergipe',
    canonical: window.location.origin + '/comprar-imovel-sergipe',
    schema: [
      generateServiceSchema(
        'Compra de Imóveis em Sergipe',
        'Serviço completo de compra de imóveis em Aracaju e Barra dos Coqueiros com atendimento especializado, suporte com financiamento e acompanhamento jurídico.'
      ),
      ...(imoveis.length > 0 ? [generatePropertyListSchema(imoveis)] : []),
      generateBreadcrumbSchema([
        { name: 'Comprar Imóvel em Sergipe', url: '/comprar-imovel-sergipe' },
      ]),
    ],
  });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb
            items={[
              { name: 'Comprar Imóvel em Sergipe', url: '/comprar-imovel-sergipe' },
            ]}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Comprar Imóvel em <span className="text-orange-500">Sergipe</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Encontre o imóvel perfeito para você e sua família em Aracaju e Barra dos Coqueiros. 
              Atendimento especializado do início ao fim.
            </p>
          </motion.div>

          {/* Benefícios */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Segurança Total</h3>
              <p className="text-gray-600">
                Acompanhamento completo da documentação e processo de compra
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-bank-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Financiamento</h3>
              <p className="text-gray-600">
                Ajudamos você a conseguir o melhor financiamento imobiliário
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-3xl text-green-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Atendimento VIP</h3>
              <p className="text-gray-600">
                Consultoria personalizada para encontrar o imóvel ideal
              </p>
            </motion.div>
          </div>

          {/* Imóveis Disponíveis */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Imóveis Disponíveis para Compra
            </h2>

            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
                {imoveis.map((imovel, index) => (
                  <motion.div
                    key={imovel.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      to={`/imovel/${imovel.slug}`}
                      className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer group"
                      data-product-shop
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={imovel.fotos?.[0]?.url || 'https://via.placeholder.com/400x300'}
                          alt={imovel.titulo}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                        {imovel.destaque && (
                          <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            Destaque
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-orange-500 uppercase">
                            {imovel.tipo}
                          </span>
                          <span className="text-xs text-gray-500">{imovel.codigo}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-orange-500 transition-colors">
                          {imovel.titulo}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 flex items-center">
                          <i className="ri-map-pin-line mr-1"></i>
                          {imovel.bairro}, {imovel.cidade}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-4 pb-4 border-b">
                          <span className="flex items-center">
                            <i className="ri-hotel-bed-line mr-1"></i>
                            {imovel.quartos}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-shower-line mr-1"></i>
                            {imovel.banheiros}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-car-line mr-1"></i>
                            {imovel.vagas}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-ruler-line mr-1"></i>
                            {imovel.area_total || imovel.area_construida}m²
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-2xl font-bold text-orange-500">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(imovel.preco)}
                            </p>
                          </div>
                          <button className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap">
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/imoveis"
                className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-full text-lg transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Ver Todos os Imóveis
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </motion.div>

          {/* Conteúdo SEO */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Como Comprar Imóvel em Sergipe com Segurança
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
              <p>
                <strong>Comprar um imóvel em Sergipe</strong> é um investimento importante que requer 
                planejamento e orientação profissional. A <strong>Mateus Oliveira Imóveis</strong> oferece 
                todo o suporte necessário para você realizar esse sonho com segurança e tranquilidade.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Vantagens de Comprar Imóvel em Aracaju e Barra dos Coqueiros
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Localização privilegiada:</strong> Próximo às melhores praias do Nordeste</li>
                <li><strong>Infraestrutura completa:</strong> Escolas, hospitais, shoppings e lazer</li>
                <li><strong>Valorização constante:</strong> Mercado imobiliário em crescimento</li>
                <li><strong>Qualidade de vida:</strong> Clima agradável e segurança</li>
                <li><strong>Investimento seguro:</strong> Imóveis de médio e alto padrão</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Nosso Processo de Compra
              </h3>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Consulta inicial:</strong> Entendemos suas necessidades e orçamento</li>
                <li><strong>Seleção de imóveis:</strong> Apresentamos as melhores opções</li>
                <li><strong>Visitas agendadas:</strong> Conhecer os imóveis pessoalmente</li>
                <li><strong>Análise financeira:</strong> Ajudamos com financiamento e documentação</li>
                <li><strong>Negociação:</strong> Conseguimos as melhores condições para você</li>
                <li><strong>Fechamento:</strong> Acompanhamento até a entrega das chaves</li>
              </ol>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Por Que Escolher a Mateus Oliveira Imóveis?
              </h3>
              <p>
                Com anos de experiência no mercado imobiliário de Sergipe, oferecemos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Atendimento personalizado e consultivo</li>
                <li>Portfólio exclusivo de imóveis de qualidade</li>
                <li>Suporte completo com financiamento</li>
                <li>Acompanhamento jurídico e documental</li>
                <li>Pós-venda com suporte contínuo</li>
              </ul>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mt-8">
                <h4 className="text-xl font-bold text-gray-800 mb-2">
                  Pronto para Comprar Seu Imóvel?
                </h4>
                <p className="mb-4">
                  Entre em contato agora mesmo e agende uma consulta gratuita. Vamos encontrar 
                  o imóvel perfeito para você em Aracaju ou Barra dos Coqueiros!
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://wa.me/5579999881002"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp (79) 99988-1002
                  </a>
                  <Link
                    to="/contato"
                    className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Enviar Mensagem
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
